// src/pages/team/PlayerStatsPage.js
import React, { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import { db, auth } from "../../firebase";
import {
  doc,
  getDoc,
  collection,
  query,
  where,
  getDocs,
  addDoc,
  updateDoc
} from "firebase/firestore";
import { useAuthState } from "react-firebase-hooks/auth";

function PlayerStatsPage() {
  const { teamId } = useParams();
  const [user] = useAuthState(auth);

  // Manager authorization
  const [canManageStats, setCanManageStats] = useState(false);

  // Completed games and roster (players)
  const [games, setGames] = useState([]);
  const [players, setPlayers] = useState([]);

  // Selections
  const [selectedGameId, setSelectedGameId] = useState("");
  const [selectedPlayerId, setSelectedPlayerId] = useState("");

  // Stats doc (if it already exists) and stat fields
  const [statsDocId, setStatsDocId] = useState("");
  const [points, setPoints] = useState("");
  const [rebounds, setRebounds] = useState("");
  const [assists, setAssists] = useState("");
  const [steals, setSteals] = useState("");
  const [blocks, setBlocks] = useState("");

  // Team name for header
  const [teamName, setTeamName] = useState("Team");
  
  // Loading and error states
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  
  // Player stats history
  const [playerStatsHistory, setPlayerStatsHistory] = useState([]);

  // -------------------
  // Enhanced Color Scheme & Style Objects
  // -------------------
  const colors = {
    primary: "#1E40AF", // Deeper blue
    accent: "#3B82F6", // Brighter blue
    background: "#F3F4F6", // Light gray background
    card: "#FFFFFF",
    text: "#1F2937", // Dark gray text
    textSecondary: "#6B7280", // Medium gray text
    border: "#E5E7EB", // Light border
    danger: "#DC2626", // Red for errors
    success: "#10B981", // Green for success
    button: {
      primary: "#2563EB",
      hover: "#1D4ED8"
    }
  };

  // Improved styles with modern aesthetic
  const pageStyles = {
    container: {
      backgroundColor: colors.background,
      minHeight: "100vh",
      padding: "40px 20px",
      display: "flex",
      justifyContent: "center",
      alignItems: "flex-start"
    },
    card: {
      backgroundColor: colors.card,
      borderRadius: "16px",
      padding: "32px",
      width: "100%",
      maxWidth: "650px",
      boxShadow: "0 10px 25px rgba(0, 0, 0, 0.05)",
      border: `1px solid ${colors.border}`
    },
    header: {
      color: colors.primary,
      fontSize: "28px",
      fontWeight: "700",
      textAlign: "center",
      marginBottom: "24px",
      padding: "0 0 16px 0",
      borderBottom: `2px solid ${colors.border}`
    },
    subHeader: {
      color: colors.text,
      fontSize: "20px",
      fontWeight: "600",
      marginBottom: "20px",
      marginTop: "16px",
      textAlign: "left"
    },
    form: {
      display: "flex",
      flexDirection: "column",
      gap: "16px"
    },
    formGroup: {
      display: "flex",
      flexDirection: "column",
      marginBottom: "4px"
    },
    statsGrid: {
      display: "grid",
      gridTemplateColumns: "repeat(2, 1fr)",
      gap: "16px",
      marginTop: "8px",
      marginBottom: "8px"
    },
    label: {
      fontSize: "15px",
      color: colors.text,
      marginBottom: "8px",
      fontWeight: "500"
    },
    input: {
      padding: "12px 16px",
      borderRadius: "8px",
      border: `1px solid ${colors.border}`,
      fontSize: "16px",
      width: "100%",
      backgroundColor: "#F9FAFB",
      transition: "border-color 0.3s, box-shadow 0.3s",
      outline: "none"
    },
    select: {
      padding: "12px 16px",
      borderRadius: "8px",
      border: `1px solid ${colors.border}`,
      fontSize: "16px",
      width: "100%",
      backgroundColor: "#F9FAFB",
      outline: "none",
      appearance: "none",
      backgroundImage: "url('data:image/svg+xml;utf8,<svg fill=\"%236B7280\" viewBox=\"0 0 20 20\" xmlns=\"http://www.w3.org/2000/svg\"><path d=\"M5.293 7.293a1 1 0 011.414 0L10 10.586l3.293-3.293a1 1 0 111.414 1.414l-4 4a1 1 0 01-1.414 0l-4-4a1 1 0 010-1.414z\"></path></svg>')",
      backgroundRepeat: "no-repeat",
      backgroundPosition: "right 12px center",
      backgroundSize: "20px 20px"
    },
    button: {
      backgroundColor: colors.button.primary,
      color: "#fff",
      border: "none",
      borderRadius: "8px",
      padding: "14px 20px",
      fontSize: "16px",
      fontWeight: "600",
      cursor: "pointer",
      transition: "all 0.2s ease",
      marginTop: "16px",
      textAlign: "center",
      boxShadow: "0 2px 4px rgba(0, 0, 0, 0.05)"
    },
    infoText: {
      textAlign: "center",
      color: colors.textSecondary,
      marginBottom: "24px",
      padding: "12px 16px",
      backgroundColor: "#F9FAFB",
      borderRadius: "8px",
      border: `1px solid ${colors.border}`,
      fontSize: "15px",
      lineHeight: "1.5"
    },
    loaderContainer: {
      display: "flex",
      justifyContent: "center",
      alignItems: "center",
      height: "100vh"
    },
    loader: {
      border: "4px solid #f3f3f3",
      borderTop: `4px solid ${colors.accent}`,
      borderRadius: "50%",
      width: "40px",
      height: "40px",
      animation: "spin 1s linear infinite"
    },
    errorText: {
      color: colors.danger,
      textAlign: "center",
      padding: "16px",
      backgroundColor: "#FEF2F2",
      borderRadius: "8px",
      border: "1px solid #FECACA",
      margin: "20px 0"
    },
    statsHistorySection: {
      marginTop: "32px"
    },
    statsHistoryTitle: {
      color: colors.text,
      fontSize: "20px",
      fontWeight: "600",
      marginBottom: "16px",
      borderBottom: `1px solid ${colors.border}`,
      paddingBottom: "8px"
    },
    statsList: {
      listStyle: "none",
      padding: 0,
      margin: 0
    },
    statsItem: {
      padding: "16px",
      borderRadius: "8px",
      backgroundColor: "#F9FAFB",
      marginBottom: "12px",
      border: `1px solid ${colors.border}`
    },
    statsHeader: {
      display: "flex",
      justifyContent: "space-between",
      alignItems: "center",
      marginBottom: "12px"
    },
    statsDate: {
      fontWeight: "600",
      color: colors.text,
      fontSize: "16px"
    },
    statsOpponent: {
      color: colors.textSecondary,
      fontSize: "14px"
    },
    statsGrid: {
      display: "grid",
      gridTemplateColumns: "repeat(5, 1fr)",
      gap: "8px",
      textAlign: "center",
      marginTop: "8px"
    },
    statBox: {
      padding: "8px 4px",
      backgroundColor: "white",
      borderRadius: "6px",
      border: `1px solid ${colors.border}`
    },
    statValue: {
      fontSize: "18px",
      fontWeight: "700",
      color: colors.primary,
      marginBottom: "2px"
    },
    statLabel: {
      fontSize: "12px",
      color: colors.textSecondary,
      textTransform: "uppercase",
      letterSpacing: "0.5px"
    },
    playerName: {
      fontSize: "16px",
      fontWeight: "600",
      color: colors.primary,
      marginBottom: "4px"
    },
    noStatsMessage: {
      textAlign: "center",
      color: colors.textSecondary,
      padding: "24px 16px",
      backgroundColor: "#F9FAFB",
      borderRadius: "8px"
    },
    debugInfo: {
      fontSize: "12px",
      color: colors.textSecondary,
      padding: "8px",
      marginTop: "24px",
      backgroundColor: "#F9FAFB",
      borderRadius: "8px",
      border: `1px solid ${colors.border}`,
      maxHeight: "200px",
      overflowY: "auto",
      display: "none" // Set to "block" for debugging
    }
  };

  // Add custom hover effect to button
  const buttonHoverStyle = {
    ...pageStyles.button,
    backgroundColor: colors.button.hover,
    boxShadow: "0 4px 6px rgba(0, 0, 0, 0.1)"
  };

  // Create a hover state for the button
  const [isButtonHovered, setIsButtonHovered] = useState(false);
  const [debugInfo, setDebugInfo] = useState([]);

  // Add debug logs for troubleshooting
  const addDebugLog = (message) => {
    setDebugInfo(prev => [...prev, `${new Date().toLocaleTimeString()}: ${message}`]);
    console.log(message);
  };

  // --------------------
  // Data Fetching
  // --------------------
  // Fetch team name for header
  useEffect(() => {
    const fetchTeamName = async () => {
      try {
        const teamRef = doc(db, "teams", teamId);
        const teamSnap = await getDoc(teamRef);
        if (teamSnap.exists()) {
          const data = teamSnap.data();
          setTeamName(data.name || "Team");
          addDebugLog(`Team name fetched: ${data.name}`);
        } else {
          addDebugLog(`Team document not found for ID: ${teamId}`);
        }
      } catch (err) {
        console.error("Error fetching team name:", err);
        addDebugLog(`Error fetching team name: ${err.message}`);
      }
    };
    fetchTeamName();
  }, [teamId]);

  // Check if user is a manager (coach)
  useEffect(() => {
    const checkManagerStatus = async () => {
      if (!user) return;
      try {
        // Try looking up in the teams collection first (direct coachId)
        const teamRef = doc(db, "teams", teamId);
        const teamSnap = await getDoc(teamRef);
        
        if (teamSnap.exists()) {
          const teamData = teamSnap.data();
          
          // Check if coach directly in the team document
          if (teamData.coachId === user.uid) {
            setCanManageStats(true);
            addDebugLog(`User is a coach: direct match in teams collection`);
            return;
          }
          
          // Also check coaches array if present
          if (teamData.coaches && Array.isArray(teamData.coaches) && 
              teamData.coaches.includes(user.uid)) {
            setCanManageStats(true);
            addDebugLog(`User is a coach: found in coaches array`);
            return;
          }
        }
        
        // Fallback to checking coaches collection
        const coachesQuery = query(
          collection(db, "coaches"),
          where("userId", "==", user.uid),
          where("teamId", "==", teamId)
        );
        const coachesSnap = await getDocs(coachesQuery);
        
        if (!coachesSnap.empty) {
          setCanManageStats(true);
          addDebugLog(`User is a coach: found in coaches collection`);
          return;
        }
        
        // No coach permissions found
        setCanManageStats(false);
        addDebugLog(`User is not a coach for this team`);
      } catch (err) {
        console.error("Error checking manager status:", err);
        addDebugLog(`Error checking manager status: ${err.message}`);
        setCanManageStats(false);
      }
    };
    checkManagerStatus();
  }, [user, teamId]);

  // Fetch completed games - with enhanced logic to handle different data models
  useEffect(() => {
    const fetchCompletedGames = async () => {
      setLoading(true);
      try {
        // Try the direct teamId approach first (new data model)
        const teamGamesQuery = query(
          collection(db, "games"),
          where("teamId", "==", teamId),
          where("status", "==", "completed")
        );
        
        const teamGamesSnap = await getDocs(teamGamesQuery);
        let allGames = teamGamesSnap.docs.map(doc => ({
          id: doc.id,
          ...doc.data(),
          querySource: "teamId" // Mark the source for debugging
        }));
        
        addDebugLog(`Games found with teamId query: ${allGames.length}`);
        
        // If no games found with new data model, try home/away approach (old data model)
        if (allGames.length === 0) {
          const homeQuery = query(
            collection(db, "games"),
            where("homeTeamId", "==", teamId),
            where("status", "==", "completed")
          );
          const awayQuery = query(
            collection(db, "games"),
            where("awayTeamId", "==", teamId),
            where("status", "==", "completed")
          );
          
          const [homeSnap, awaySnap] = await Promise.all([
            getDocs(homeQuery),
            getDocs(awayQuery)
          ]);
          
          const homeGames = homeSnap.docs.map(doc => ({
            id: doc.id,
            ...doc.data(),
            querySource: "homeTeamId"
          }));
          
          const awayGames = awaySnap.docs.map(doc => ({
            id: doc.id,
            ...doc.data(),
            querySource: "awayTeamId"
          }));
          
          allGames = [...homeGames, ...awayGames];
          addDebugLog(`Games found with home/away queries: ${allGames.length} (Home: ${homeGames.length}, Away: ${awayGames.length})`);
        }
        
        // Look for games where this team might be in a "teams" array
        // This handles a potential third data model where teams are stored in an array
        if (allGames.length === 0) {
          const arrayTeamsQuery = query(
            collection(db, "games"),
            where("teams", "array-contains", teamId),
            where("status", "==", "completed")
          );
          
          const arrayTeamsSnap = await getDocs(arrayTeamsQuery);
          const arrayTeamsGames = arrayTeamsSnap.docs.map(doc => ({
            id: doc.id,
            ...doc.data(),
            querySource: "teams-array"
          }));
          
          allGames = arrayTeamsGames;
          addDebugLog(`Games found with teams array query: ${allGames.length}`);
        }
        
        // Sort games by date (most recent first)
        allGames.sort((a, b) => {
          // Safely handle date comparison
          const dateA = a.date ? new Date(a.date) : new Date(0);
          const dateB = b.date ? new Date(b.date) : new Date(0);
          return dateB - dateA;
        });
        
        // Log game info for debugging
        allGames.forEach((game, idx) => {
          addDebugLog(`Game ${idx+1}: ID=${game.id}, Date=${game.date}, Source=${game.querySource}`);
        });
        
        setGames(allGames);
        setLoading(false);
      } catch (err) {
        console.error("Error fetching completed games:", err);
        addDebugLog(`Error fetching games: ${err.message}`);
        setError("Failed to fetch completed games");
        setLoading(false);
      }
    };
    fetchCompletedGames();
  }, [teamId]);

  // Helper functions to get opponent name based on game data model
  const getOpponentName = (game) => {
    // If the game has a direct opponentName property, use it
    if (game.opponentName) return game.opponentName;
    
    // Handle home/away model
    if (game.homeTeamId === teamId && game.awayTeamName) {
      return game.awayTeamName;
    }
    
    if (game.awayTeamId === teamId && game.homeTeamName) {
      return game.homeTeamName;
    }
    
    // Last resort
    return "Opponent";
  };

  // Helper to get score from game data
  const getGameScore = (game) => {
    if (game.score) return game.score; // If there's a direct score property
    
    const isHome = game.homeTeamId === teamId;
    
    // If homeScore and awayScore exist
    if (typeof game.homeScore !== 'undefined' && typeof game.awayScore !== 'undefined') {
      return isHome 
        ? `${game.homeScore} - ${game.awayScore}`
        : `${game.awayScore} - ${game.homeScore}`;
    }
    
    // Fallback
    return "0-0";
  };

  // Helper to get game result (W/L/T)
  const getGameResult = (game) => {
    // New model: Using result property directly if it exists
    if (game.result) return game.result;
    
    // Old model: Calculate based on home/away and scores
    const isHome = game.homeTeamId === teamId;
    
    if (typeof game.homeScore !== 'undefined' && typeof game.awayScore !== 'undefined') {
      if (isHome) {
        if (game.homeScore > game.awayScore) return "W";
        if (game.homeScore < game.awayScore) return "L";
        return "T";
      } else {
        if (game.awayScore > game.homeScore) return "W";
        if (game.awayScore < game.homeScore) return "L";
        return "T";
      }
    }
    
    // Fallback
    return "";
  };

  // Fetch roster (players) - Enhanced to handle multiple data models
  useEffect(() => {
    const fetchRoster = async () => {
      try {
        // First try getting players from the rosters collection
        const rosterQ = query(
          collection(db, "rosters"),
          where("teamId", "==", teamId)
        );
        const rosterSnap = await getDocs(rosterQ);
        
        if (rosterSnap.empty) {
          addDebugLog("No roster entries found in rosters collection");
        } else {
          addDebugLog(`Found ${rosterSnap.docs.length} roster entries`);
        }
        
        // Process player data from rosters
        const playerPromises = rosterSnap.docs.map(async (rDoc) => {
          const rosterData = rDoc.data();
          const { playerId } = rosterData;
          
          // Skip if no playerId
          if (!playerId) {
            addDebugLog(`Roster entry missing playerId: ${rDoc.id}`);
            return null;
          }
          
          try {
            // Try to get player from the players collection
            const playerRef = doc(db, "players", playerId);
            const pSnap = await getDoc(playerRef);
            
            if (pSnap.exists()) {
              const playerData = pSnap.data();
              // Ensure we have name data
              if (!playerData.firstName && !playerData.lastName && rosterData.playerName) {
                // Try to split playerName from roster into firstName/lastName
                const nameParts = rosterData.playerName.split(' ');
                const firstName = nameParts[0] || '';
                const lastName = nameParts.slice(1).join(' ') || '';
                
                return { 
                  id: playerId, 
                  ...playerData,
                  firstName: firstName,
                  lastName: lastName 
                };
              }
              
              return { id: playerId, ...playerData };
            } else {
              // Player not found in players collection
              addDebugLog(`Player not found for ID: ${playerId}`);
              
              // Try to use data from roster entry if available
              if (rosterData.playerName) {
                const nameParts = rosterData.playerName.split(' ');
                return {
                  id: playerId,
                  firstName: nameParts[0] || '',
                  lastName: nameParts.slice(1).join(' ') || '',
                  number: rosterData.number || '',
                  position: rosterData.position || ''
                };
              }
            }
          } catch (err) {
            addDebugLog(`Error fetching player ${playerId}: ${err.message}`);
          }
          
          return null;
        });
        
        const results = await Promise.all(playerPromises);
        
        // Filter out null values and sort by last name
        const sortedPlayers = results
          .filter((p) => p !== null)
          .sort((a, b) => {
            // Handle missing lastName fields
            const lastNameA = a.lastName || '';
            const lastNameB = b.lastName || '';
            return lastNameA.localeCompare(lastNameB);
          });
        
        addDebugLog(`Final player count: ${sortedPlayers.length}`);
        setPlayers(sortedPlayers);
      } catch (err) {
        console.error("Error fetching roster:", err);
        addDebugLog(`Roster fetch error: ${err.message}`);
        setError("Failed to fetch team roster");
      }
    };
    fetchRoster();
  }, [teamId]);

  // Load existing stats doc if it exists
  useEffect(() => {
    const fetchExistingStats = async () => {
      if (!selectedGameId || !selectedPlayerId) {
        setStatsDocId("");
        setPoints("");
        setRebounds("");
        setAssists("");
        setSteals("");
        setBlocks("");
        return;
      }
      
      try {
        const statsQ = query(
          collection(db, "playerStats"),
          where("gameId", "==", selectedGameId),
          where("playerId", "==", selectedPlayerId)
        );
        const statsSnap = await getDocs(statsQ);
        if (!statsSnap.empty) {
          const existing = statsSnap.docs[0];
          const data = existing.data();
          setStatsDocId(existing.id);
          setPoints(data.points?.toString() || "");
          setRebounds(data.rebounds?.toString() || "");
          setAssists(data.assists?.toString() || "");
          setSteals(data.steals?.toString() || "");
          setBlocks(data.blocks?.toString() || "");
          addDebugLog(`Existing stats found for game ${selectedGameId}, player ${selectedPlayerId}`);
        } else {
          setStatsDocId("");
          setPoints("");
          setRebounds("");
          setAssists("");
          setSteals("");
          setBlocks("");
          addDebugLog(`No existing stats found for game ${selectedGameId}, player ${selectedPlayerId}`);
        }
      } catch (err) {
        console.error("Error fetching existing stats:", err);
        addDebugLog(`Error fetching existing stats: ${err.message}`);
      }
    };
    fetchExistingStats();
  }, [selectedGameId, selectedPlayerId]);

  // Fetch player stats history when a player is selected
  useEffect(() => {
    const fetchPlayerStatsHistory = async () => {
      if (!selectedPlayerId) {
        setPlayerStatsHistory([]);
        return;
      }
      
      try {
        // Query player stats for this player
        const statsQ = query(
          collection(db, "playerStats"),
          where("playerId", "==", selectedPlayerId)
        );
        const statsSnap = await getDocs(statsQ);
        
        if (statsSnap.empty) {
          setPlayerStatsHistory([]);
          addDebugLog(`No stats history found for player ${selectedPlayerId}`);
          return;
        }
        
        addDebugLog(`Found ${statsSnap.docs.length} stat records for player ${selectedPlayerId}`);
        
        // Process each stats document
        const statsData = await Promise.all(
          statsSnap.docs.map(async (statDoc) => {
            const statData = statDoc.data();
            const gameId = statData.gameId;
            
            if (!gameId) {
              addDebugLog(`Stat record ${statDoc.id} missing gameId`);
              return null;
            }
            
            try {
              // Get game details
              const gameRef = doc(db, "games", gameId);
              const gameSnap = await getDoc(gameRef);
              
              if (gameSnap.exists()) {
                const gameData = gameSnap.data();
                
                // Determine if this game belongs to the current team
                const isTeamGame = 
                  gameData.teamId === teamId || 
                  gameData.homeTeamId === teamId || 
                  gameData.awayTeamId === teamId ||
                  (gameData.teams && gameData.teams.includes(teamId));
                
                if (!isTeamGame) {
                  addDebugLog(`Game ${gameId} does not belong to team ${teamId}`);
                  return null;
                }
                
                return {
                  id: statDoc.id,
                  ...statData,
                  game: {
                    id: gameId,
                    date: gameData.date,
                    opponentName: getOpponentName(gameData),
                    homeTeamId: gameData.homeTeamId,
                    awayTeamId: gameData.awayTeamId,
                    homeScore: gameData.homeScore,
                    awayScore: gameData.awayScore,
                    result: getGameResult(gameData),
                    score: getGameScore(gameData)
                  }
                };
              } else {
                addDebugLog(`Game ${gameId} not found for stat record ${statDoc.id}`);
                return null;
              }
            } catch (err) {
              addDebugLog(`Error processing stat record ${statDoc.id}: ${err.message}`);
              return null;
            }
          })
        );
        
        // Filter out nulls and sort by game date
        const sortedStats = statsData
          .filter(stat => stat !== null)
          .sort((a, b) => {
            const dateA = a.game.date ? new Date(a.game.date) : new Date(0);
            const dateB = b.game.date ? new Date(b.game.date) : new Date(0);
            return dateB - dateA;
          });
        
        addDebugLog(`Processed ${sortedStats.length} valid stat records`);
        setPlayerStatsHistory(sortedStats);
      } catch (err) {
        console.error("Error fetching player stats history:", err);
        addDebugLog(`Error fetching stats history: ${err.message}`);
      }
    };
    
    fetchPlayerStatsHistory();
  }, [selectedPlayerId, teamId, games]);

  const handleSaveStats = async (e) => {
    e.preventDefault();
    if (!canManageStats) {
      alert("You are not authorized to add/edit stats.");
      return;
    }
    if (!selectedGameId || !selectedPlayerId) {
      alert("Please select a game and a player first.");
      return;
    }
    try {
      const data = {
        gameId: selectedGameId,
        playerId: selectedPlayerId,
        teamId: teamId, // Add teamId for easier querying
        points: parseInt(points || "0"),
        rebounds: parseInt(rebounds || "0"),
        assists: parseInt(assists || "0"),
        steals: parseInt(steals || "0"),
        blocks: parseInt(blocks || "0"),
        timestamp: new Date().toISOString()
      };
      
      addDebugLog(`Saving stats for game ${selectedGameId}, player ${selectedPlayerId}`);
      
      if (statsDocId) {
        // Update existing stats
        await updateDoc(doc(db, "playerStats", statsDocId), data);
        alert("Player stats updated successfully!");
        addDebugLog(`Updated existing stats document ${statsDocId}`);
      } else {
        // Create new stats
        const docRef = await addDoc(collection(db, "playerStats"), data);
        setStatsDocId(docRef.id);
        alert("Player stats created successfully!");
        addDebugLog(`Created new stats document ${docRef.id}`);
      }
      
      // Refresh the player stats history
      const selectedPlayer = players.find(p => p.id === selectedPlayerId);
      const selectedGame = games.find(g => g.id === selectedGameId);
      
      if (selectedPlayer && selectedGame) {
        const newStat = {
          ...data,
          id: statsDocId || "temp-id", // Will be replaced on next fetch
          game: {
            id: selectedGameId,
            date: selectedGame.date,
            opponentName: getOpponentName(selectedGame),
            homeTeamId: selectedGame.homeTeamId,
            awayTeamId: selectedGame.awayTeamId,
            homeScore: selectedGame.homeScore,
            awayScore: selectedGame.awayScore,
            result: getGameResult(selectedGame),
            score: getGameScore(selectedGame)
          }
        };
        
        // Update local state to immediately reflect changes
        const updatedHistory = statsDocId
          ? playerStatsHistory.map(stat => 
              stat.gameId === selectedGameId && stat.playerId === selectedPlayerId 
                ? { ...stat, ...data } 
                : stat
            )
// Continuing from previous file
: [newStat, ...playerStatsHistory];
          
setPlayerStatsHistory(updatedHistory);
}
} catch (err) {
console.error("Error saving stats:", err);
addDebugLog(`Error saving stats: ${err.message}`);
alert("Failed to save player stats.");
}
};

// Format date for display
const formatGameDate = (dateString) => {
if (!dateString) return "No date";

try {
const options = { weekday: 'short', month: 'short', day: 'numeric', year: 'numeric' };
return new Date(dateString).toLocaleDateString(undefined, options);
} catch (err) {
addDebugLog(`Error formatting date ${dateString}: ${err.message}`);
return "Invalid date";
}
};

// Get selected player's name
const getSelectedPlayerName = () => {
if (!selectedPlayerId) return '';
const player = players.find(p => p.id === selectedPlayerId);
if (!player) return '';

// Handle cases where first/last name might be missing
if (player.firstName && player.lastName) {
return `${player.firstName} ${player.lastName}`;
} else if (player.firstName) {
return player.firstName;
} else if (player.lastName) {
return player.lastName;
} else if (player.displayName) {
return player.displayName;
} else {
return 'Player';
}
};

if (loading) {
return (
<div style={pageStyles.loaderContainer}>
<div style={pageStyles.loader}></div>
</div>
);
}

if (error) {
return <div style={pageStyles.container}>
<div style={pageStyles.card}>
<h2 style={pageStyles.header}>{teamName} Player Stats</h2>
<div style={pageStyles.errorText}>{error}</div>
</div>
</div>;
}

return (
<div style={pageStyles.container}>
<div style={pageStyles.card}>
<h2 style={pageStyles.header}>{teamName} Player Statistics</h2>

{!canManageStats ? (
  <div style={pageStyles.infoText}>
    Only the team manager can add or edit player statistics. Viewing player statistics only.
  </div>
) : (
  <>
    <h3 style={pageStyles.subHeader}>Record Player Game Stats</h3>
    <form onSubmit={handleSaveStats} style={pageStyles.form}>
      <div style={pageStyles.formGroup}>
        <label style={pageStyles.label}>Completed Game:</label>
        <select
          style={pageStyles.select}
          value={selectedGameId}
          onChange={(e) => setSelectedGameId(e.target.value)}
          required
        >
          <option value="">-- Select a completed game --</option>
          {games.length === 0 ? (
            <option value="" disabled>No completed games found</option>
          ) : (
            games.map((g) => (
              <option key={g.id} value={g.id}>
                {formatGameDate(g.date)} vs {getOpponentName(g)}
              </option>
            ))
          )}
        </select>
      </div>
      
      <div style={pageStyles.formGroup}>
        <label style={pageStyles.label}>Player:</label>
        <select
          style={pageStyles.select}
          value={selectedPlayerId}
          onChange={(e) => setSelectedPlayerId(e.target.value)}
          required
        >
          <option value="">-- Select a player --</option>
          {players.length === 0 ? (
            <option value="" disabled>No players found</option>
          ) : (
            players.map((p) => (
              <option key={p.id} value={p.id}>
                {p.firstName || ""} {p.lastName || ""}
                {p.number ? ` (#${p.number})` : ""}
              </option>
            ))
          )}
        </select>
      </div>
      
      <div style={{
        display: "grid",
        gridTemplateColumns: "repeat(auto-fill, minmax(120px, 1fr))",
        gap: "16px",
        marginTop: "8px"
      }}>
        <div style={pageStyles.formGroup}>
          <label style={pageStyles.label}>Points:</label>
          <input
            type="number"
            min="0"
            style={pageStyles.input}
            value={points}
            onChange={(e) => setPoints(e.target.value)}
            placeholder="0"
          />
        </div>
        
        <div style={pageStyles.formGroup}>
          <label style={pageStyles.label}>Rebounds:</label>
          <input
            type="number"
            min="0"
            style={pageStyles.input}
            value={rebounds}
            onChange={(e) => setRebounds(e.target.value)}
            placeholder="0"
          />
        </div>
        
        <div style={pageStyles.formGroup}>
          <label style={pageStyles.label}>Assists:</label>
          <input
            type="number"
            min="0"
            style={pageStyles.input}
            value={assists}
            onChange={(e) => setAssists(e.target.value)}
            placeholder="0"
          />
        </div>
        
        <div style={pageStyles.formGroup}>
          <label style={pageStyles.label}>Steals:</label>
          <input
            type="number"
            min="0"
            style={pageStyles.input}
            value={steals}
            onChange={(e) => setSteals(e.target.value)}
            placeholder="0"
          />
        </div>
        
        <div style={pageStyles.formGroup}>
          <label style={pageStyles.label}>Blocks:</label>
          <input
            type="number"
            min="0"
            style={pageStyles.input}
            value={blocks}
            onChange={(e) => setBlocks(e.target.value)}
            placeholder="0"
          />
        </div>
      </div>
      
      <button 
        type="submit" 
        style={isButtonHovered ? buttonHoverStyle : pageStyles.button}
        onMouseEnter={() => setIsButtonHovered(true)}
        onMouseLeave={() => setIsButtonHovered(false)}
      >
        {statsDocId ? "Update Player Stats" : "Record Player Stats"}
      </button>
    </form>
  </>
)}

{/* Player Stats History Section */}
{selectedPlayerId && (
  <div style={pageStyles.statsHistorySection}>
    <h3 style={pageStyles.statsHistoryTitle}>
      {getSelectedPlayerName()}'s Game Stats
    </h3>
    
    {playerStatsHistory.length === 0 ? (
      <div style={pageStyles.noStatsMessage}>
        No recorded stats for this player yet.
      </div>
    ) : (
      <ul style={pageStyles.statsList}>
        {playerStatsHistory.map((stat) => {
          const game = stat.game || {};
          const result = game.result || "";
          
          return (
            <li key={stat.id} style={pageStyles.statsItem}>
              <div style={pageStyles.statsHeader}>
                <div>
                  <div style={pageStyles.statsDate}>
                    {formatGameDate(game.date)} {result ? `(${result})` : ""}
                  </div>
                  <div style={pageStyles.statsOpponent}>
                    vs {game.opponentName || "Opponent"} • {game.score || ""}
                  </div>
                </div>
              </div>
              
              <div style={pageStyles.statsGrid}>
                <div style={pageStyles.statBox}>
                  <div style={pageStyles.statValue}>{stat.points || 0}</div>
                  <div style={pageStyles.statLabel}>PTS</div>
                </div>
                <div style={pageStyles.statBox}>
                  <div style={pageStyles.statValue}>{stat.rebounds || 0}</div>
                  <div style={pageStyles.statLabel}>REB</div>
                </div>
                <div style={pageStyles.statBox}>
                  <div style={pageStyles.statValue}>{stat.assists || 0}</div>
                  <div style={pageStyles.statLabel}>AST</div>
                </div>
                <div style={pageStyles.statBox}>
                  <div style={pageStyles.statValue}>{stat.steals || 0}</div>
                  <div style={pageStyles.statLabel}>STL</div>
                </div>
                <div style={pageStyles.statBox}>
                  <div style={pageStyles.statValue}>{stat.blocks || 0}</div>
                  <div style={pageStyles.statLabel}>BLK</div>
                </div>
              </div>
            </li>
          );
        })}
      </ul>
    )}
  </div>
)}

{/* Debug Information Section (hidden by default) */}
<div style={pageStyles.debugInfo}>
  <h4>Debug Info:</h4>
  <ul>
    {debugInfo.map((log, idx) => (
      <li key={idx}>{log}</li>
    ))}
  </ul>
</div>
</div>
</div>
);
}

export default PlayerStatsPage;